import { defineStore } from "pinia";
import  { ItemInstancesDocument, ItemsDocument, TopicDetailsDocument, type TopicInstance } from "~/graphql";
import { useFeaturesManagement } from "~/stores/featuresManagement";
import { useRemoveItemMutation, useReorderItemMutation, useUpdateItemMutation } from "~/composables";
import { gql } from "@urql/vue";

const updateTopicStatistics = (
  cache: any,
  topicId: string,
  val: number,
  topicInstanceId?: string,
) => {
  if (topicInstanceId) {
    const fragment = gql`
      fragment _ on TopicInstance {
        id
        statistics {
          totalItemInstances
        }
      }
    `;

    const topicInstance = cache.readFragment(fragment, {
      id: topicInstanceId,
    }) as TopicInstance;

    if (topicInstance) {
      const data = { ...topicInstance };
      if (data.statistics) {
        data.statistics.totalItemInstances += val;
      }
      cache.writeFragment(fragment, data);
    }
  }

  /*
  // find topic fragment in cache
  const fragment = gql`
    fragment _ on Topic {
      id
      topicStatistics {
        linkedItemsTotal
      }
    }
  `;

  const topic = cache.readFragment(fragment, { id: topicId }) as Topic;

  if (topic) {
    const data = { ...topic };
    if (data.topicStatistics) {
      data.topicStatistics.linkedItemsTotal += val;
    }
    cache.writeFragment(fragment, data);
  }*/
};
export const itemCacheExchange = {
  updates: {
    Mutation: {
      reorderItem(result, _args, cache) {
        const itemManagment = useItemManagement();
        const topicInstanceId = itemManagment.topicInstanceId;
        const itemId = result.reorderItem.item.id;
        const position = _args.itemReorderInput.position;

        if (topicInstanceId) {
          cache.updateQuery(
            {
              query: ItemInstancesDocument,
              variables: { topicInstanceIdInput: { id: topicInstanceId } },
            },
            (data) => {
              if (data) {
                let itemInstance = null;
                for (let i = 0; i < data.itemInstances.length; i++) {
                  if (data.itemInstances[i]?.item.id === itemId) {
                    itemInstance = data.itemInstances.splice(i, 1)[0];
                  }
                }
                data.itemInstances.splice(position, 0, itemInstance);
              }
              return data;
            },
          );
        } else {
          cache.updateQuery(
            {
              query: ItemsDocument,
              variables: {
                topicIdInput: { id: _args.itemReorderInput.topicId },
              },
            },
            (data) => {
              if (data) {
                let item = null;
                for (let i = 0; i < data.items.length; i++) {
                  if (data.items[i]?.id === itemId) {
                    item = data.items.splice(i, 1)[0];
                  }
                }
                data.items.splice(position, 0, item);
              }
              return data;
            },
          );
          cache.updateQuery(
            {
              query: TopicDetailsDocument,
              variables: { topicId: _args.itemReorderInput.topicId },
            },
            (data) => {
              if (data) {
                let item = null;
                for (let i = 0; i < data.items.length; i++) {
                  if (data.items[i]?.id === itemId) {
                    item = data.items.splice(i, 1)[0];
                  }
                }
                data.items.splice(position, 0, item);
              }
              return data;
            },
          );
        }

        //itemManagment.clearTopicInstanceId();
      },
      removeItem(result, _args, cache) {
        const itemId = _args.topicItemRemoveInput.id;
        const itemManagment = useItemManagement();
        const topicInstanceId = itemManagment.topicInstanceId;
        const topicId = _args.topicItemRemoveInput.topicId;
        cache.invalidate({ __typename: 'Item', id: itemId });
        updateTopicStatistics(cache, topicId, -1, topicInstanceId as string);
        itemManagment.clearTopicInstanceId();
      },
    },
  },
};

export const useItemManagement = defineStore('itemManagement2', () => {
  const featuresStore = useFeaturesManagement();
  featuresStore.init();
  const updateItemMutation = useUpdateItemMutation();
  const removeItemMutation = useRemoveItemMutation();
  const reorderItemMutation = useReorderItemMutation();
  const topicInstanceId = useState<string | null>(
    'topicInstanceId',
    () => null,
  );

  const clearTopicInstanceId = () => {
    topicInstanceId.value = null;
  };
  const updateItem = async (itemId: string, updatedFields: object) => {
    const result = await updateItemMutation.executeMutation({
      updateItemInput: {
        id: itemId,
        ...updatedFields,
      },
    });

    if (result.error) {
      console.log(result.error);
      throw result.error;
    }

    return result.data?.updateItem.item;
  };


  const removeItem = async (
    itemId: string,
    topicId: string,
    forceDelete: boolean,
    topicInstanceIdValue?: string,
  ) => {
    if (topicInstanceIdValue) {
      topicInstanceId.value = topicInstanceIdValue;
    }

    const result = await removeItemMutation.executeMutation({
      topicItemInput: {
        id: itemId,
        topicId,
        forceDelete,
      },
    });

    if (result.error) {
      throw result.error;
    }
    return result.data?.removeItem.done;
  };

  const reorderItem = async (
    topicId: string,
    topicInstanceIdValue: string,
    itemId: string,
    position: number,
  ) => {
    if (topicInstanceIdValue) {
      topicInstanceId.value = topicInstanceIdValue;
    }
    const result = await reorderItemMutation.executeMutation({
      itemReorderInput: {
        id: itemId,
        position,
        topicId,
      },
    });

    if (result.error) {
      console.log(result.error);
      throw result.error;
    }

    return result.data?.reorderItem.item;
  };

  return {
    topicInstanceId,
    updateItem,
    removeItem,
    reorderItem,
    clearTopicInstanceId,
  };
});
